import { useEffect, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { uploadImages } from '@/api/upload';
import Select from '@/components/base/Select';

interface OrderItem {
  productId: string;
  goodsId: string;
  specId: string;
  name: string;
  quantity: number;
  unitPrice: number;
  unit: string;
  spec?: string;
}

export interface AfterSaleProductPayload {
  productId: string;
  goodsId: string;
  specId: string;
  productName: string;
  quantity: number;
  unit: string;
  unitPrice: number;
}

export interface AfterSaleSubmitPayload {
  orderId: string;
  products: AfterSaleProductPayload[];
  /** 原生售后类型：退货 / 换货 */
  type: '退货' | '换货';
  reason: string;
  description: string;
  /** 已上传的售后图片 URL，最多 5 张 */
  images: string[];
}

interface AfterSaleModalProps {
  isOpen: boolean;
  orderId: string;
  items: OrderItem[];
  submitting?: boolean;
  onCancel: () => void;
  onSubmit: (data: AfterSaleSubmitPayload) => void | Promise<void>;
}

interface PreviewItem {
  id: string;
  file: File;
  url: string;
}

const MAX_IMAGES = 5;
const MAX_SIZE = 10 * 1024 * 1024;

const reasonOptions = [
  '商品质量问题',
  '型号规格不符',
  '商品损坏/破损',
  '发错商品/规格',
  '商品与描述不符',
  '数量不符',
  '其他',
];

export default function AfterSaleModal({
  isOpen,
  orderId,
  items,
  submitting = false,
  onCancel,
  onSubmit,
}: AfterSaleModalProps) {
  const [type, setType] = useState<'退货' | '换货'>('退货');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [imageItems, setImageItems] = useState<PreviewItem[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setType('退货');
    setSelectedIds([]);
    setQuantities({});
    setReason('');
    setDescription('');
    setImageItems((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.url));
      return [];
    });
    setDragOver(false);
    setUploading(false);
    setErrors({});
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [isOpen, orderId]);

  if (!isOpen) return null;

  const remain = MAX_IMAGES - imageItems.length;
  const busy = uploading || submitting;

  const isSelected = (productId: string) => selectedIds.includes(productId);

  const toggleSelect = (item: OrderItem) => {
    setErrors((prev) => {
      const next = { ...prev };
      delete next.products;
      delete next[`qty-${item.productId}`];
      return next;
    });

    if (isSelected(item.productId)) {
      setSelectedIds((prev) => prev.filter((id) => id !== item.productId));
      return;
    }

    setSelectedIds((prev) => [...prev, item.productId]);
    setQuantities((prev) => ({
      ...prev,
      [item.productId]: prev[item.productId] ?? 1,
    }));
  };

  const setQty = (item: OrderItem, nextQty: number) => {
    if (!isSelected(item.productId)) return;
    const maxQty = item.quantity;
    const qty = Math.min(maxQty, Math.max(1, nextQty));
    setQuantities((prev) => ({ ...prev, [item.productId]: qty }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`qty-${item.productId}`];
      return next;
    });
  };

  const appendFiles = (fileList: FileList | File[]) => {
    const incoming = Array.from(fileList);
    if (incoming.length === 0) return;

    setErrors((prev) => {
      const next = { ...prev };
      delete next.images;
      return next;
    });

    if (remain <= 0) {
      setErrors((prev) => ({ ...prev, images: `最多上传 ${MAX_IMAGES} 张图片` }));
      return;
    }

    const next: PreviewItem[] = [];
    let rejectReason = '';

    for (const file of incoming) {
      if (next.length >= remain) {
        rejectReason = `最多上传 ${MAX_IMAGES} 张图片，已自动截取前 ${MAX_IMAGES} 张`;
        break;
      }
      if (!file.type.startsWith('image/')) {
        rejectReason = '仅支持图片格式（JPG、PNG、WEBP等）';
        continue;
      }
      if (file.size > MAX_SIZE) {
        rejectReason = '单张图片不能超过 10MB';
        continue;
      }
      next.push({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        url: URL.createObjectURL(file),
      });
    }

    if (next.length > 0) {
      setImageItems((prev) => [...prev, ...next]);
    }
    if (rejectReason) {
      setErrors((prev) => ({ ...prev, images: rejectReason }));
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) appendFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files) appendFiles(e.dataTransfer.files);
  };

  const removeImage = (id: string) => {
    setImageItems((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter((p) => p.id !== id);
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next.images;
      return next;
    });
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (selectedIds.length === 0) {
      errs.products = '请至少选择一件商品';
    }
    if (!reason) errs.reason = '请选择售后原因';

    let validCount = 0;
    selectedIds.forEach((id) => {
      const item = items.find((i) => i.productId === id);
      if (!item) return;
      const qty = quantities[id] ?? 0;
      if (!item.goodsId || !item.specId) {
        errs[`qty-${id}`] = '商品信息不完整，无法申请售后';
        return;
      }
      if (qty < 1) {
        errs[`qty-${id}`] = '售后数量至少为1';
        return;
      }
      if (qty > item.quantity) {
        errs[`qty-${id}`] = `最多 ${item.quantity} ${item.unit}`;
        return;
      }
      validCount += 1;
    });

    if (selectedIds.length > 0 && validCount === 0) {
      errs.products = '请至少选择一件商品并填写有效售后数量';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || busy) return;

    const products: AfterSaleProductPayload[] = selectedIds
      .map((id) => {
        const item = items.find((i) => i.productId === id);
        if (!item) return null;
        const quantity = quantities[id] ?? 0;
        if (quantity < 1 || !item.goodsId || !item.specId) return null;
        return {
          productId: item.productId,
          goodsId: item.goodsId,
          specId: item.specId,
          productName: item.name,
          quantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
        };
      })
      .filter(Boolean) as AfterSaleProductPayload[];

    if (products.length === 0) {
      setErrors((prev) => ({
        ...prev,
        products: '请至少选择一件商品并填写售后数量',
      }));
      return;
    }

    let imageUrls: string[] = [];
    if (imageItems.length > 0) {
      setUploading(true);
      try {
        const res = await uploadImages(imageItems.map((i) => i.file));
        const ok = Number(res.errno) === 0 || res.error === false || Number(res.error) === 0;
        const urls = Array.isArray(res.data) ? res.data.filter(Boolean) : [];
        if (!ok || urls.length === 0) {
          setErrors((prev) => ({
            ...prev,
            images: res.message || res.msg || '图片上传失败，请重试',
          }));
          return;
        }
        imageUrls = urls.slice(0, MAX_IMAGES);
      } catch {
        setErrors((prev) => ({ ...prev, images: '图片上传失败，请稍后重试' }));
        return;
      } finally {
        setUploading(false);
      }
    }

    await onSubmit({
      orderId,
      products,
      type,
      reason,
      description: description.trim(),
      images: imageUrls,
    });
  };

  const handleClose = () => {
    if (busy) return;
    setType('退货');
    setSelectedIds([]);
    setQuantities({});
    setReason('');
    setDescription('');
    setImageItems((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.url));
      return [];
    });
    setErrors({});
    onCancel();
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40">
      <div className="bg-background-50 rounded-xl w-full max-w-3xl mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-background-200 flex items-center gap-3 sticky top-0 bg-background-50 z-10">
          <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-primary-100">
            <i className="ri-service-line text-primary-600 text-lg" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-foreground-900">售后申请</h3>
            <p className="text-xs text-foreground-400 mt-0.5">支持多选商品，可上传最多 {MAX_IMAGES} 张售后图片</p>
          </div>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="bg-background-100 rounded-lg p-3">
            <p className="text-xs text-foreground-500">关联订单</p>
            <p className="text-sm font-medium text-foreground-800 mt-0.5">{orderId}</p>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
            <div className="w-full sm:flex-1 min-w-0">
              <label className="text-sm font-medium text-foreground-800 mb-2 block">售后类型</label>
              <Select
                value={type}
                disabled={busy}
                onChange={(v) => setType(v as '退货' | '换货')}
                  options={[
                  { label: '退货', value: '退货' },
                  { label: '换货', value: '换货' },
                ]}
              />
            </div>

            <div className="w-full sm:flex-1 min-w-0">
              <label className="text-sm font-medium text-foreground-800 mb-2 block">售后原因</label>
              <Select
                value={reason}
                disabled={busy}
                invalid={!!errors.reason}
                placeholder="请选择原因"
                onChange={(v) => {
                  setReason(v);
                  setErrors((prev) => ({ ...prev, reason: '' }));
                }}
                options={reasonOptions.map((r) => ({ label: r, value: r }))}
              />
              {errors.reason && (
                <p className="text-xs text-primary-600 mt-1.5">{errors.reason}</p>
              )}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2 gap-2">
              <label className="text-sm font-medium text-foreground-800">选择商品</label>
              <span className="text-xs text-foreground-400">
                已选 {selectedIds.length}/{items.length}
              </span>
            </div>

            <div className="rounded-lg border border-background-200 overflow-hidden">
              <div className="hidden sm:grid grid-cols-[auto_minmax(0,1fr)_auto] gap-3 px-3 py-2 bg-background-100 text-xs text-foreground-500 border-b border-background-200">
                <span className="w-5" />
                <span>商品信息</span>
                <span className="w-[11.5rem] text-right pr-1">售后数量</span>
              </div>

              <div className="divide-y divide-background-100 max-h-64 overflow-y-auto">
                {items.map((item) => {
                  const checked = isSelected(item.productId);
                  const qty = quantities[item.productId] ?? 1;
                  const qtyError = errors[`qty-${item.productId}`];

                  return (
                    <div
                      key={item.productId}
                      className={`grid grid-cols-1 sm:grid-cols-[auto_minmax(0,1fr)_auto] gap-2 sm:gap-3 items-center px-3 py-3 transition-colors ${
                        checked ? 'bg-primary-50/60' : 'bg-background-50 hover:bg-background-100/60'
                      }`}
                    >
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => toggleSelect(item)}
                        className="flex items-start sm:items-center gap-3 text-left min-w-0 sm:contents"
                      >
                        <span
                          className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 mt-0.5 sm:mt-0 ${
                            checked
                              ? 'border-primary-500 bg-primary-500 text-background-50'
                              : 'border-background-300 bg-white text-transparent'
                          }`}
                        >
                          <i className="ri-check-line text-xs" />
                        </span>

                        <span className="min-w-0 flex-1 sm:flex-none">
                          <span className="block text-sm text-foreground-800 truncate">{item.name}</span>
                          <span className="block text-xs text-foreground-500 mt-0.5">
                            {item.spec ? `${item.spec} · ` : ''}
                            ¥{item.unitPrice.toFixed(2)}/{item.unit} · 已购 {item.quantity} {item.unit}
                          </span>
                          {qtyError && (
                            <span className="block text-xs text-primary-600 mt-1 sm:hidden">{qtyError}</span>
                          )}
                        </span>
                      </button>

                      <div className="pl-8 sm:pl-0 flex items-center justify-end gap-1.5">
                        <button
                          type="button"
                          disabled={!checked || busy}
                          onClick={() => setQty(item, qty - 1)}
                          className="w-8 h-8 rounded-lg border border-background-300 flex items-center justify-center hover:bg-background-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        >
                          <i className="ri-subtract-line text-foreground-600 text-sm" />
                        </button>
                        <input
                          type="number"
                          min={1}
                          max={item.quantity}
                          disabled={!checked || busy}
                          value={checked ? qty : ''}
                          placeholder="-"
                          onChange={(e) =>
                            setQty(item, Math.min(item.quantity, Math.max(1, parseInt(e.target.value, 10) || 1)))
                          }
                          className="w-14 text-center py-1.5 text-sm border border-background-300 rounded-lg outline-none focus:border-primary-400 disabled:bg-background-100 disabled:text-foreground-400 disabled:cursor-not-allowed"
                        />
                        <button
                          type="button"
                          disabled={!checked || busy}
                          onClick={() => setQty(item, qty + 1)}
                          className="w-8 h-8 rounded-lg border border-background-300 flex items-center justify-center hover:bg-background-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                        >
                          <i className="ri-add-line text-foreground-600 text-sm" />
                        </button>
                        <span className="text-[10px] text-foreground-400 w-12 text-left shrink-0">
                          /{item.quantity}
                        </span>
                      </div>

                      {qtyError && (
                        <p className="hidden sm:block sm:col-span-3 text-xs text-primary-600 -mt-1 pl-8">
                          {qtyError}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {errors.products && (
              <p className="text-xs text-primary-600 mt-1.5">{errors.products}</p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2 gap-2">
              <label className="text-sm font-medium text-foreground-800">售后图片（选填）</label>
              <span className="text-xs text-foreground-400">
                {imageItems.length}/{MAX_IMAGES}
              </span>
            </div>

            {imageItems.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-2">
                {imageItems.map((item) => (
                  <div
                    key={item.id}
                    className="relative aspect-square rounded-lg overflow-hidden border border-background-200 bg-background-100"
                  >
                    <img src={item.url} alt="售后图片预览" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => removeImage(item.id)}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-background-50 transition-colors disabled:opacity-50"
                    >
                      <i className="ri-close-line text-xs" />
                    </button>
                  </div>
                ))}
                {remain > 0 && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => fileInputRef.current?.click()}
                    className="aspect-square rounded-lg border-2 border-dashed border-background-300 hover:border-primary-300 hover:bg-background-50 flex flex-col items-center justify-center gap-1 text-foreground-400 transition-colors disabled:opacity-50"
                  >
                    <i className="ri-add-line text-lg" />
                    <span className="text-[10px]">继续添加</span>
                  </button>
                )}
              </div>
            )}

            {imageItems.length === 0 && (
              <div
                onDrop={handleDrop}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors ${
                  dragOver
                    ? 'border-primary-400 bg-primary-50'
                    : 'border-background-300 hover:border-primary-300 hover:bg-background-50'
                }`}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className="w-10 h-10 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-2">
                  <i className="ri-image-add-line text-lg text-foreground-400" />
                </div>
                <p className="text-sm text-foreground-600 mb-1">拖拽图片到此处，或点击上传</p>
                <p className="text-xs text-foreground-400">支持一次多选，最多 {MAX_IMAGES} 张，单张不超过 10MB</p>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              className="hidden"
            />

            {errors.images && (
              <p className="text-xs text-primary-600 mt-1.5">{errors.images}</p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-foreground-800 mb-2 block">详细说明（选填）</label>
            <textarea
              value={description}
              disabled={busy}
              onChange={(e) => setDescription(e.target.value.slice(0, 500))}
              rows={3}
              placeholder="请详细描述售后问题..."
              maxLength={500}
              className="w-full px-3 py-2.5 rounded-lg border border-background-300 text-sm outline-none resize-none focus:border-primary-400 transition-colors disabled:bg-background-100"
            />
            <p className="text-xs text-foreground-400 text-right mt-1">{description.length}/500</p>
          </div>
        </div>

        <div className="px-5 py-4 border-t border-background-200 flex items-center justify-end gap-2 sticky bottom-0 bg-background-50">
          <button
            type="button"
            disabled={busy}
            onClick={handleClose}
            className="px-4 py-2 rounded-lg border border-background-300 text-foreground-600 text-sm hover:bg-background-100 transition-colors whitespace-nowrap disabled:opacity-50"
          >
            取消
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => void handleSubmit()}
            className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors whitespace-nowrap inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {busy && <i className="ri-loader-4-line animate-spin text-sm" />}
            {uploading ? '上传中...' : submitting ? '提交中...' : `提交申请${selectedIds.length > 0 ? `（${selectedIds.length}）` : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
